import React, { useCallback, useState } from 'react';
import { InputView, InputViewProps, OptionalTextArea } from './Input.view';
import classNames from 'classnames';
import inputClasses from '../form.module.scss';
import { InputTypes } from '../../../constants/form';
import { FormElementAction, FormElementValueType, SetValueFunction } from '../../../models/basic-objects';

export type OptionalInputAction =
  | {
      updateAction?: FormElementAction<unknown>;
      updateDebounceTiming?: never;
    }
  | {
      updateAction: FormElementAction<unknown>;
      updateDebounceTiming?: number;
    };

export type InputControllerProps = Omit<
  InputViewProps,
  | 'errorClasses'
  | 'executing'
  | 'globalClasses'
  | 'helperClass'
  | 'iconClasses'
  | 'labelClasses'
  | 'outerClasses'
  | 'updateValue'
  | 'debounceTiming'
  | 'type'
> & {
  type?: InputTypes;
  wrapper?: boolean;
  inputWrapperClassName?: string;
} & OptionalTextArea &
  OptionalInputAction;

export const InputController = (props: InputControllerProps): JSX.Element => {
  const [executing, setExecuting] = useState<boolean>(false);
  const clonedProps: InputControllerProps = { ...props };

  clonedProps.disabled = props.disabled || false;
  clonedProps.helper = props.helper || '';
  clonedProps.inlinedLabel = (props.inlinedLabel && props.size === 'large') || false;
  clonedProps.readonly = props.readonly || executing;
  clonedProps.multilineResizable = props.multilineResizable || false;

  delete clonedProps.type;
  const type: InputTypes = props.type ? props.type : InputTypes.text;
  const { formInstance, updateAction, name } = props;
  const { setValue: setFormValue, trigger: triggerValidation, getValues: getFormValues } = formInstance;

  const outerClasses = classNames(
    {
      'input-wrapper': props.wrapper,
      hide: type === InputTypes.hidden
    },
    clonedProps.inputWrapperClassName
  );
  const globalClasses = classNames(inputClasses.FormInputs, inputClasses[clonedProps.size], {
    [inputClasses.largeInnerInput]: clonedProps.inlinedLabel,
    [inputClasses.FormInputsErrorClass]:
      !!formInstance.formState.errors && formInstance.formState.errors.hasOwnProperty(props.name),
    [inputClasses.FormInputsTextarea]: props.multiline,
    [inputClasses.FormInputsTextareaNonResizable]: !props.multilineResizable
  });
  const helperClass = classNames(inputClasses.HelpText, clonedProps.inlinedLabel ? inputClasses.HelpTextInlined : null);
  const errorClasses = classNames(
    inputClasses.HelpTextErrorClass,
    inputClasses.HelpText,
    clonedProps.inlinedLabel ? inputClasses.HelpTextInlined : null
  );
  const labelClasses = classNames({
    [inputClasses.Label]: true,
    [inputClasses.LabelInner]: clonedProps.inlinedLabel,
    [inputClasses.LabelDisabled]: clonedProps.disabled
  });
  const inputClassname = `inputWithIcon${props.inlinedLabel && props.size === 'large' ? 'inner-' : ''}${props.size}`;
  const iconClasses = classNames({
    [inputClasses[inputClassname]]: !!(props.actionButton && props.action) || executing
  });

  const setValue: SetValueFunction = useCallback(
    async (value, oldValue) => {
      if ((value || value === '') && oldValue !== value) {
        setFormValue(name, value, { shouldValidate: false, shouldTouch: true });
        try {
          const result = await triggerValidation(name);
          if (result && updateAction) {
            setExecuting(true);
            updateAction(value || '', (keepNewVal = true) => {
              if (!keepNewVal) {
                setFormValue(name, oldValue, {
                  shouldValidate: true,
                  shouldTouch: true
                });
              }
              setExecuting(false);
            });
          }
        } catch (e) {
          // prevents runtime
        }
      }
    },
    [updateAction, name, setExecuting, setFormValue, triggerValidation]
  );

  const updateValue = useCallback(
    async (value: FormElementValueType) => {
      const originalValue: FormElementValueType = getFormValues(name);
      if (originalValue !== value) {
        try {
          await setValue(value, originalValue);
        } catch (e) {
          // prevents runtime error
        }
      }
    },

    [getFormValues, name, setValue]
  );

  const attrs: InputViewProps = {
    ...clonedProps,
    debounceTiming: updateAction ? props.updateDebounceTiming || 500 : 0,
    errorClasses,
    executing,
    globalClasses,
    helperClass,
    iconClasses,
    labelClasses,
    outerClasses,
    type,
    updateValue
  };

  return <InputView {...attrs} />;
};
